
import React, { forwardRef, useEffect, useRef } from 'react';
import { TextInput as DefaultTextInput, ActivityIndicator, Text as DefaultText, View as DefaultView, TouchableOpacity, useWindowDimensions, type TouchableOpacityProps, useColorScheme } from 'react-native';
import { useColors } from '../constants/useColors';
import { MyTextStyles, MyViewStyles } from '../constants/styles';

type BETextProps = {
  theme?: 'h1'|'h2'|'description'|'light'|'warning'|'success'|'error'|'action',
  size?:number,
  color?:string,
  textAlign?:'left'|'center'|'right',
  weight?: 'bold'|'semibold'|'regular'|'light'
};

export type TextProps = BETextProps & DefaultText['props'];


const STANDARD_SIZE = 414

export function Text(props: TextProps) {
  const { theme, size, color, textAlign, weight, style, ...otherProps } = props;
    const C = useColors();

const dimensions = useWindowDimensions();


  const getStyle = () => {
    switch(theme){
        case 'h1': return MyTextStyles.h1
        case 'h2': return MyTextStyles.h2
        case 'description': return MyTextStyles.description
        case 'light': return MyTextStyles.descriptionLight
        default: return MyTextStyles.description
    }
  }

  const getColor = () => {
    if(color){ return color }
    switch(theme){
        case 'h1': return C.text.h1
        case 'h2': return C.text.h2
        case 'description': return C.text.description
        case 'action': return C.text.action
        case 'light': return C.text.descriptionLight
        case 'success': return C.text.success
        case 'error': return C.text.error
        case 'warning': return C.text.warning
        default: return C.text.description
    }
  }

  const resize = (fontSize:number) => {
    let rendered_size = size ?? fontSize
    let diff_from_normal = dimensions.width - STANDARD_SIZE
    if(diff_from_normal < 0){
        let pct_diff = Math.abs(diff_from_normal / STANDARD_SIZE)
        rendered_size = rendered_size * (1 - pct_diff)
    }
    return rendered_size
  }

  const myStyle = getStyle();
  const myColor = getColor() ;
  const mySize = resize(myStyle.fontSize);


  return <DefaultText style={[myStyle, { color: myColor, fontSize:mySize, textAlign }, style]} {...otherProps} />;
}


type BEViewProps = {
  type?: 'header'|'body'|'footer'|'input'|'blur'|'dark_blur'|'row',
  transparent?:boolean,
  float?:boolean
}
export type ViewProps = BEViewProps & DefaultView['props'];


export const View = forwardRef(({ ...props }:ViewProps, ref:any) => {
  const { style, float, transparent, type, ...otherProps } = props;
  const color_mode = useColorScheme() ?? 'light'
  const C = useColors();
  
  const getColor = () => {
    if(transparent){ return 'transparent' }
    switch(type){
      case 'header': return C.views.header
      case 'body': return C.views.background
      case 'footer': return C.views.footer
      case 'input': return C.views.input
      case 'blur': return C.views.blur
      case 'dark_blur': return C.views.dark_blur
      default: return C.views.background 
    }
  }
  const color = getColor();
  let border_color = type == 'input' ? C.borders.input : undefined
  let border_width = type == 'input' ? 1 : undefined
  const borderRadius = type=='input' ? 8 : undefined
  let floated = (float || type=='input') ? color_mode == 'dark' ? MyViewStyles.floatDark : MyViewStyles.floatLight : {}
  let flexDirection:'row'|'column' = type == 'row' ? 'row' : 'column'
  let alignItems:'center'|undefined = type == 'row' ? 'center' : undefined
  return <DefaultView nativeID={props.nativeID} ref={ref} style={[{ backgroundColor:color, borderRadius, borderColor: border_color, borderWidth: border_width, ...floated }, { flexDirection, alignItems }, style]} {...otherProps} />;
})


type BEButtonProps = {
  float?:boolean,
  nativeID?:string,
  backgroundColor?:string,
  title?:string,
  loading?:boolean,
  transparent?:boolean,
  title_color?:string,
  borderColor?:string,
  type?: 'info'|'close'|'success'|'warning'|'error'|'text'|'squares'|'competition'|'action'
}
export type ButtonProps = BEButtonProps & TouchableOpacityProps;



export const Button = (props:ButtonProps) => {
  const { style, type, children, transparent, loading, title, float, backgroundColor, title_color, borderColor, ...otherProps } = props;
  const color_mode = useColorScheme() ?? 'light'
  const C = useColors();

  const getTheme = () => {
    let b_colors = {
        background_color: backgroundColor ?? C.buttons.background.info,
        text_color: title_color ?? C.buttons.text.info,
        border_color: borderColor ?? C.buttons.borders.info,
        border_width: 0
    }
    switch(type){
        case 'squares':
          return {
            background_color: C.buttons.background.squares,
            text_color: C.text.h1,
            border_width: 0,
            border_color:'transparent'
          }
        case 'competition':
          return {
            background_color: C.buttons.background.competition,
            text_color: C.text.h1,
            border_width: 0,
            border_color:'transparent'
          }
        case 'action':
          return {
            background_color:C.buttons.background.action,
            text_color:C.text.white,
            border_width:0,
            border_color:'transparent'
          }
        case 'text':
          return {
            background_color: 'transparent',
            text_color: C.text.action,
            border_width: 0,
            border_color:'transparent'
          }
        case 'warning':
          return {
                background_color: C.buttons.background.warning,
                text_color: C.text.white,
                border_color: C.buttons.borders.info,
                border_width:0
          }
        case 'info':
            b_colors.border_width = 1
            return b_colors
        case 'close':
            return {
                background_color: C.buttons.background.close,
                text_color: C.buttons.text.close,
                border_color: C.buttons.borders.close,
                border_width:1
            }
        case 'success':
            return {
                background_color: C.buttons.background.success,
                text_color: C.buttons.text.success,
                border_color: C.buttons.borders.success,
                border_width:0
            }
        case 'error':
            return {
                background_color: C.buttons.background.error,
                text_color: C.buttons.text.error,
                border_color: C.buttons.borders.error,
                border_width:0
            }
        default: return b_colors

    }
}

const { background_color, text_color, border_color, border_width } = getTheme();
let floated = float ? color_mode == 'dark' ? MyViewStyles.floatDark : MyViewStyles.floatLight : {}

  return (
    <TouchableOpacity 
      nativeID={props.nativeID}
    style={[{ borderRadius:8, backgroundColor:!transparent?background_color:'transparent', borderColor:border_color, borderWidth: border_width, padding:15, opacity:loading?0.5:1, ...floated }, style]} 
      disabled={loading || otherProps.disabled}
      { ...otherProps }
      >
      {title?
      <Text color={text_color} theme='h2' textAlign='center'>{title}</Text>
      :<></>}
      {children}
      {loading ?
      <View transparent style={{ position:'absolute', top:0, left:0, right:0, bottom:0, justifyContent:'center', alignItems:'center' }}>
        <ActivityIndicator color={text_color} size='small'/>
      </View>
      :<></>}
    </TouchableOpacity>
  )
}


type BETextInputProps = {
  type?:string,
  loading?:boolean,
  focus_on_unload?:boolean,
  transparent?:boolean,
  weight?:'bold'|'semibold'|'regular'
  onFocusPosition?: (position:{x:number, y:number, width:number, height:number}) => void
}

type TextInputProps = BETextInputProps & DefaultTextInput['props']

export const TextInput = (props:TextInputProps) => {
  const C = useColors();
  const inputRef = useRef<DefaultTextInput>(null);

  useEffect(() => {
    if(!props.loading && props.focus_on_unload){
      inputRef.current?.focus()
    }
  },[props.loading])

  const getFamily = () => {
    switch(props.weight){
      case 'bold': return 'BarlowBold'
      case 'semibold': return 'BarlowSemiBold'
      default: return 'BarlowRegular'
    }
  }

  const handleFocus = (e:any) => {
    if(props.onFocus){ props.onFocus(e) }
    if(!props.onFocusPosition){ return }
    if (inputRef.current && props.onFocusPosition) {
      inputRef.current.measureInWindow((x, y, width, height) => {
        if(props.onFocusPosition){
          props.onFocusPosition({x, y, width, height})
        }
      });
    }
  }

  const { type, style, transparent, ...otherProps } = props;
  
  const background = transparent ? undefined : props.editable == false ? C.views.disabled_input : C.views.input
  const border = transparent ? undefined : C.borders.input
  const color = props.editable == false ? C.text.descriptionLight :C.text.input
  const borderWidth = transparent ? 0 : 1
  const placeholder_color = C.text.placholder
  const font_family = getFamily();
  return (
    <DefaultTextInput 
      ref={inputRef}
      multiline={props.multiline}
      onSubmitEditing={props.onSubmitEditing}
      placeholderTextColor={placeholder_color} 
      style={[{ padding:15, backgroundColor: background, borderColor: border, borderWidth, color, fontFamily: font_family }, style]} 
      { ...otherProps }
      onFocus={handleFocus}
    />
  )
}

